/**
 * Bottle alerts on this device, as pure rules: whether the browser can get
 * them, what the Settings switch shows, and which baby a tapped alert opens.
 * The browser calls are in push.ts; these are unit-tested.
 */
import { type BottleCheck, bottleCheck } from "../sw/notification.ts";

/**
 * - `ok`: this browser can turn alerts on.
 * - `home-screen`: an iPhone or iPad outside the Home Screen app. Web Push
 *   works only once Babia is added to the Home Screen and opened from there
 *   (iOS 16.4+), even where a tab exposes the push APIs.
 * - `unsupported`: no Web Push in this browser.
 */
export type PushSupport = "ok" | "home-screen" | "unsupported";

export function pushSupport({
  hasPush,
  isIos,
  standalone,
}: {
  hasPush: boolean;
  isIos: boolean;
  standalone: boolean;
}): PushSupport {
  if (isIos && !standalone) return "home-screen";
  return hasPush ? "ok" : "unsupported";
}

/** What the Settings switch shows, first match wins. */
export type AlertsView =
  /** This server sends no alerts (no key): the section is hidden. */
  | "hidden"
  | "unsupported"
  | "home-screen"
  /** The trial ended unpaid: alerts pause until someone buys. */
  | "paused"
  /** Paused, on a device the server still has: it can be turned off, not on. */
  | "paused-on"
  /** The parent said no to notifications; only the browser's settings can undo that. */
  | "blocked"
  /** Blocked, on a device the server still has: it can be turned off, not on. */
  | "blocked-on"
  | "on"
  | "off";

export function alertsView({
  configured,
  support,
  permission,
  subscribed,
  canLog,
}: {
  configured: boolean;
  support: PushSupport;
  permission: NotificationPermission | null;
  subscribed: boolean;
  canLog: boolean;
}): AlertsView {
  if (!configured) return "hidden";
  if (support !== "ok") return support;
  // A device the server still has can always be turned off, whatever the permission.
  if (!canLog) return subscribed ? "paused-on" : "paused";
  if (permission === "denied") return subscribed ? "blocked-on" : "blocked";
  return subscribed && permission === "granted" ? "on" : "off";
}

/** A tapped alert's `/?baby=…&from=push&check=…`: the baby to show, and what to record. */
export function alertFromSearch(search: string): {
  babyId: string | null;
  fromPush: boolean;
  check: BottleCheck | null;
} {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { babyId: null, fromPush: false, check: null };
  }
  const babyId = params.get("baby");
  return {
    babyId: babyId && /^[0-9a-f-]{36}$/i.test(babyId) ? babyId : null,
    fromPush: params.get("from") === "push",
    // Only the server's names reach analytics, whatever the URL says.
    check: bottleCheck(params.get("check")),
  };
}

/**
 * Where sign-in returns to. A tapped alert that finds the session expired
 * lands on the signed-out page at `/?baby=…`; returning to a bare `/` would
 * open the remembered baby instead of the alert's. Only the values
 * `alertFromSearch` accepts go back into the address.
 */
export function alertReturnPath(location: { pathname: string; search: string }): string {
  if (location.pathname !== "/") return "/";
  const alert = alertFromSearch(location.search);
  const params = new URLSearchParams();
  if (alert.babyId) params.set("baby", alert.babyId);
  if (alert.fromPush) params.set("from", "push");
  if (alert.check) params.set("check", alert.check);
  const search = params.toString();
  return search ? `/?${search}` : "/";
}

/**
 * Turn a subscription off on both sides. The browser goes first: once it has
 * unsubscribed, the push service answers the next alert with 410 and the
 * server drops the device, so alerts stop even when the server can't be told
 * now (offline, or signing out). Never throws.
 */
export async function forgetSubscription(
  subscription: { endpoint: string; unsubscribe: () => Promise<boolean> },
  forget: (endpoint: string) => Promise<unknown>,
): Promise<{ unsubscribed: boolean; forgotten: boolean }> {
  const unsubscribed = await subscription.unsubscribe().catch(() => false);
  const forgotten = await forget(subscription.endpoint).then(
    () => true,
    () => false,
  );
  return { unsubscribed, forgotten };
}

/** The browser's `applicationServerKey` from the server's base64url public key. */
export function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Whether a subscription was made with this key (a rotated key needs a new one). */
export function sameKey(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.byteLength) return false;
  const view = new Uint8Array(a);
  return view.every((byte, i) => byte === b[i]);
}
